import { findFirstNonEmptyString, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useImageUtils } from "~/lib/hooks";
import { formatInTimeZone } from "~/lib/utils";
import type { itemOtherScmpSeriesSeriesArticleArticle$key } from "~/queries/__generated__/itemOtherScmpSeriesSeriesArticleArticle.graphql";

import {
  Container,
  Content,
  StyledBaseImage,
  StyledContentBookmark,
  StyledEntityLink,
  StyledSeriesArticleSectionTag,
  Time,
  Title,
  Toolbar,
} from "./styles";

export type Props = {
  className?: string;
  reference: itemOtherScmpSeriesSeriesArticleArticle$key;
};

export const SeriesArticleOtherScmpSeriesItem: FunctionComponent<Props> = ({
  className,
  reference,
}) => {
  const { getResizedImageUrl } = useImageUtils();

  const queueItem = useFragment(
    graphql`
      fragment itemOtherScmpSeriesSeriesArticleArticle on Article {
        ...sectionTagSeriesArticleArticle
        ...entityLink
        entityId
        headline
        socialHeadline
        updatedDate
        images {
          title
          url
          isSlideshow
          style(filter: { style: "540x360" }) {
            url
          }
        }
      }
    `,
    reference,
  );

  const renderHeadline = () =>
    findFirstNonEmptyString(queueItem.socialHeadline, queueItem.headline);

  const renderImage = () => {
    const images = queueItem.images?.filter(img => notEmpty(img)) ?? [];
    const mainImage = images.find(img => !!img?.isSlideshow) ?? first(images);
    const source = getResizedImageUrl(mainImage?.style?.url ?? mainImage?.url ?? "", 540);
    const alt = mainImage?.title ?? "";
    return (
      <StyledBaseImage
        $aspectRatio="540 / 360"
        $height="auto"
        $objectFit="cover"
        alt={alt}
        src={source}
        disableLinkingData
      />
    );
  };

  const renderContentBookmark = () => (
    <StyledContentBookmark entityId={queueItem.entityId} key="content-bookmark" location="side" />
  );

  return (
    <Container className={className}>
      <StyledEntityLink reference={queueItem} />
      {renderImage()}
      <Content>
        <div>
          <StyledSeriesArticleSectionTag reference={queueItem} />
          <Title>{renderHeadline()}</Title>
        </div>

        <Toolbar>
          {queueItem?.updatedDate && (
            <Time>{formatInTimeZone(queueItem.updatedDate, "d MMM yyyy - h:mmb")}</Time>
          )}
          {renderContentBookmark()}
        </Toolbar>
      </Content>
    </Container>
  );
};

SeriesArticleOtherScmpSeriesItem.displayName = "SeriesArticleOtherScmpSeriesItem";
